import React, { useState, useEffect, useMemo } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  InputBase,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip,
  alpha,
  useTheme,
} from "@mui/material";
import {
  Close as CloseIcon,
  Search as SearchIcon,
  TrendingUp as TrendingIcon,
  History as HistoryIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { quickAccessItems } from "../../navigation/modernNavigations";

const CommandPalette = ({ open, onClose }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [recentItems, setRecentItems] = useState([]);

  // Filter items based on search query
  const filteredItems = useMemo(() => {
    if (!searchQuery) return [];

    const query = searchQuery.toLowerCase();
    return quickAccessItems.filter(
      (item) =>
        item.title.toLowerCase().includes(query) ||
        item.description.toLowerCase().includes(query) ||
        item.module.toLowerCase().includes(query)
    );
  }, [searchQuery]);

  // Load recent items from localStorage
  useEffect(() => {
    const stored = localStorage.getItem("recentNavigations");
    if (stored) {
      setRecentItems(JSON.parse(stored));
    }
  }, []);

  const handleNavigate = (item) => {
    // Save to recent items
    const newRecent = [
      item,
      ...recentItems.filter((r) => r.path !== item.path),
    ].slice(0, 5);
    setRecentItems(newRecent);
    localStorage.setItem("recentNavigations", JSON.stringify(newRecent));

    // Navigate
    navigate(item.path);
    onClose();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      onClose();
    } else if (e.key === "Enter" && filteredItems.length > 0) {
      handleNavigate(filteredItems[0]);
    }
  };

  return (
    <Drawer
      anchor="top"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          maxWidth: 700,
          mx: "auto",
          mt: 8,
          borderRadius: 3,
          boxShadow: theme.shadows[20],
        },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: alpha(theme.palette.common.black, 0.7),
          backdropFilter: "blur(8px)",
        },
      }}
    >
      <Box sx={{ p: 2 }}>
        {/* Search Input */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            backgroundColor: alpha(theme.palette.common.black, 0.05),
            borderRadius: 2,
            px: 2,
            py: 1.5,
            mb: 2,
          }}
        >
          <SearchIcon sx={{ mr: 1.5, color: "text.secondary" }} />
          <InputBase
            autoFocus
            fullWidth
            placeholder="Search for modules, pages, or actions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            sx={{ fontSize: 16 }}
          />
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Search Results */}
        {searchQuery && (
          <Box sx={{ maxHeight: 400, overflow: "auto" }}>
            {filteredItems.length > 0 ? (
              <List disablePadding>
                {filteredItems.map((item, index) => (
                  <ListItem key={index} disablePadding>
                    <ListItemButton
                      onClick={() => handleNavigate(item)}
                      sx={{
                        borderRadius: 1.5,
                        mb: 0.5,
                        "&:hover": {
                          backgroundColor: alpha(item.moduleColor, 0.08),
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          color: item.moduleColor,
                          minWidth: 40,
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.title}
                        secondary={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              mt: 0.5,
                            }}
                          >
                            <Chip
                              label={item.module}
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: 11,
                                backgroundColor: alpha(item.moduleColor, 0.1),
                                color: item.moduleColor,
                              }}
                            />
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {item.description}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            ) : (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  No results found for "{searchQuery}"
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Recent & Popular when no search */}
        {!searchQuery && (
          <Box>
            {/* Recent */}
            {recentItems.length > 0 && (
              <>
                <Box
                  sx={{ display: "flex", alignItems: "center", mb: 1, px: 1 }}
                >
                  <HistoryIcon
                    sx={{ fontSize: 18, mr: 1, color: "text.secondary" }}
                  />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                  >
                    RECENT
                  </Typography>
                </Box>
                <List disablePadding>
                  {recentItems.map((item, index) => (
                    <ListItem key={index} disablePadding>
                      <ListItemButton
                        onClick={() => handleNavigate(item)}
                        sx={{ borderRadius: 1.5, mb: 0.5 }}
                      >
                        <ListItemIcon
                          sx={{ color: item.moduleColor, minWidth: 40 }}
                        >
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={item.title}
                          secondary={item.module}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
                <Divider sx={{ my: 2 }} />
              </>
            )}

            {/* Quick Tips */}
            <Box sx={{ px: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <TrendingIcon
                  sx={{ fontSize: 18, mr: 1, color: "text.secondary" }}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={600}
                >
                  QUICK TIPS
                </Typography>
              </Box>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ mb: 0.5 }}
              >
                • Press <kbd>Ctrl+K</kbd> or <kbd>⌘K</kbd> to open this palette
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ mb: 0.5 }}
              >
                • Start typing to search across all modules
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                • Press <kbd>Enter</kbd> to navigate to the top result
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default CommandPalette;
